import React, { useState, useEffect } from 'react';
import { Calendar, Users, Clock, Briefcase } from 'lucide-react';
import api from '../lib/api';

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'status-pending',
    confirmed: 'status-confirmed',
    completed: 'status-completed',
    cancelled: 'status-cancelled'
  };

  return (
    <span className={`px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs font-bold uppercase whitespace-nowrap ${styles[status] || ''}`}>
      {status}
    </span>
  );
};

const OverviewTab = ({ bookings, customers, messages }) => {
  const [jobs, setJobs] = useState([]);
  const [staff, setStaff] = useState([]);
  
  useEffect(() => {
    api.admin.getJobs().then(r => setJobs(r.data)).catch(() => {});
    api.admin.getStaff().then(r => setStaff(r.data)).catch(() => {});
  }, []);

  const pendingBookings = bookings.filter(b => b.status === 'pending').length;
  const unreadMessages = messages.filter(m => !m.is_read).length;
  const unpaidJobs = jobs.filter(j => j.payment_status !== 'paid' && j.job_status !== 'cancelled');
  const totalOutstanding = unpaidJobs.reduce((s, j) => s + ((j.final_bill_amount || j.estimate_total || 0) - (j.amount_paid || 0)), 0);
  const todayStr = new Date().toISOString().split('T')[0];
  const todaysBookings = bookings.filter(b => b.preferred_date === todayStr);
  const staffMap = {};
  staff.forEach(s => { staffMap[s.id] = s; });
  
  const stats = [
    { label: 'Total Bookings', value: bookings.length, icon: Calendar },
    { label: 'Pending', value: pendingBookings, icon: Clock },
    { label: 'Customers', value: customers.length, icon: Users },
    { label: 'Outstanding', value: `$${totalOutstanding.toLocaleString('en-US', { minimumFractionDigits: 0 })}`, icon: Briefcase, highlight: totalOutstanding > 0 }
  ];

  return (
    <div className="space-y-4 md:space-y-6" data-testid="overview-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {stats.map((stat) => (
          <div key={stat.label} className={`bg-secondary-dark p-4 md:p-6 rounded-lg border ${stat.highlight ? 'border-red-500/30' : 'border-white/10'}`}>
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <stat.icon className={`w-6 h-6 md:w-8 md:h-8 ${stat.highlight ? 'text-red-400' : 'text-electric-teal'}`} />
            </div>
            <p className={`text-xl md:text-3xl font-bold ${stat.highlight ? 'text-red-400' : 'text-white'}`}>{stat.value}</p>
            <p className="text-white/60 text-xs md:text-sm mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Today's Schedule */}
      <div className="bg-secondary-dark p-4 md:p-6 rounded-lg border border-electric-teal/20" data-testid="todays-schedule">
        <h3 className="text-electric-teal font-bold mb-3 md:mb-4 flex items-center gap-2 text-sm md:text-base">
          <Calendar className="w-4 h-4 md:w-5 md:h-5" /> Today&apos;s Schedule — {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </h3>
        {todaysBookings.length > 0 ? (
          <div className="space-y-2 md:space-y-3">
            {todaysBookings.sort((a, b) => (a.preferred_time || '').localeCompare(b.preferred_time || '')).map((b) => {
              const est = staffMap[b.assigned_estimator_id];
              return (
                <div key={b.id} className="flex flex-wrap md:flex-nowrap items-center gap-2 md:gap-4 p-2 md:p-3 bg-white/5 rounded-lg">
                  <div className="text-electric-teal font-mono font-bold text-xs md:text-sm w-14 md:w-16">{b.preferred_time}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm md:text-base truncate">{b.customer_name}</p>
                    <p className="text-white/50 text-xs truncate">{b.service_type}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {est && (
                      <span className="flex items-center gap-1 text-xs">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: est.color }} />
                        <span className="text-white/50 hidden sm:inline">{est.name.split(' ')[0]}</span>
                      </span>
                    )}
                    <StatusBadge status={b.status} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-white/30 text-sm">No appointments scheduled for today.</p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        {/* Outstanding Payments */}
        {unpaidJobs.length > 0 && (
          <div className="bg-secondary-dark p-4 md:p-6 rounded-lg border border-red-500/20">
            <h3 className="text-red-400 font-bold mb-3 md:mb-4 text-sm md:text-base">Outstanding Payments</h3>
            <div className="space-y-2 md:space-y-3">
              {unpaidJobs.slice(0, 5).map((j) => (
                <div key={j.id} className="flex items-center justify-between p-2 md:p-3 bg-white/5 rounded-lg gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium text-sm truncate">{j.customer_name}</p>
                    <p className="text-white/50 text-xs truncate">{j.job_number} - {j.service_type}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-red-400 font-bold text-sm md:text-base">${((j.final_bill_amount || j.estimate_total || 0) - (j.amount_paid || 0)).toFixed(2)}</p>
                    <p className="text-white/30 text-xs">{j.payment_status}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Bookings */}
        <div className="bg-secondary-dark p-4 md:p-6 rounded-lg border border-white/10">
          <h3 className="text-white font-bold mb-3 md:mb-4 text-sm md:text-base">Recent Bookings</h3>
          <div className="space-y-2 md:space-y-3">
            {bookings.slice(0, 5).map((booking) => (
              <div key={booking.id} className="flex items-center justify-between p-2 md:p-3 bg-white/5 rounded-lg gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-white font-medium text-sm truncate">{booking.customer_name}</p>
                  <p className="text-white/60 text-xs truncate">{booking.service_type}</p>
                </div>
                <StatusBadge status={booking.status} />
              </div>
            ))}
          </div>
        </div>

        {/* Recent Messages */}
        <div className="bg-secondary-dark p-4 md:p-6 rounded-lg border border-white/10">
          <h3 className="text-white font-bold mb-3 md:mb-4 text-sm md:text-base">Recent Messages</h3>
          <div className="space-y-2 md:space-y-3">
            {messages.slice(0, 5).map((message) => (
              <div key={message.id} className="p-2 md:p-3 bg-white/5 rounded-lg">
                <div className="flex items-center justify-between mb-1 md:mb-2">
                  <p className="text-white font-medium text-sm truncate">{message.name}</p>
                  {!message.is_read && (
                    <span className="w-2 h-2 bg-electric-teal rounded-full flex-shrink-0"></span>
                  )}
                </div>
                <p className="text-white/60 text-xs md:text-sm line-clamp-2">{message.message}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export { StatusBadge };
export default OverviewTab;
